const SCROLL_TRIGGER = 'scroll-trigger';
const SCROLL_OFFSCREEN = 'scroll-trigger--offscreen';
const ZOOM_TRIGGER = 'animate--zoom-in';
const ANIMATION_CANCEL = 'scroll-trigger--cancel';

function onIntersection(entries, observer) {
  entries.forEach((entry, index) => {
    const element = entry.target;

    if (entry.isIntersecting) {
      element.classList.remove(SCROLL_OFFSCREEN);

      if (element.hasAttribute('data-cascade')) {
        element.style.setProperty('--animation-order', index);
      }

      element.classList.add('xuyel-visible');
      observer.unobserve(element);
    }
  });
}

function initializeScrollAnimationTrigger(
  rootEl = document,
  isDesignModeEvent = false
) {
  const elements = Array.from(
    rootEl.getElementsByClassName(SCROLL_TRIGGER)
  );

  if (!elements.length) return;

  if (isDesignModeEvent) {
    elements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -12% 0px',
    threshold: 0.08
  });

  elements.forEach((element) => {
    element.classList.add(SCROLL_OFFSCREEN);
    observer.observe(element);
  });
}


/* =========================================
   XUYEL PREMIUM IMAGE ZOOM
   ========================================= */

function initializeScrollZoomAnimationTrigger() {
  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }

  const elements = Array.from(
    document.getElementsByClassName(ZOOM_TRIGGER)
  );

  if (!elements.length) return;

  elements.forEach((element) => {
    let visible = false;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visible = entry.isIntersecting;
        });
      },
      {
        threshold: 0
      }
    );

    observer.observe(element);

    const updateZoom = () => {
      if (!visible) return;

      const progress = percentageSeen(element);

      const zoom = 1 + (progress * 0.0015);

      element.style.setProperty(
        '--zoom-in-ratio',
        Math.min(1.15, zoom)
      );
    };

    window.addEventListener(
      'scroll',
      throttle(updateZoom, 16),
      {
        passive: true
      }
    );

    updateZoom();
  });
}


/* =========================================
   XUYEL PARALLAX
   ========================================= */

function initializeXuyelParallax() {
  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }

  const elements = document.querySelectorAll(
    '[data-xuyel-parallax]'
  );

  if (!elements.length) return;

  const updateParallax = () => {
    const viewportHeight = window.innerHeight;

    elements.forEach((element) => {
      const rect = element.getBoundingClientRect();

      if (
        rect.bottom < 0 ||
        rect.top > viewportHeight
      ) {
        return;
      }

      const center =
        rect.top + rect.height / 2;

      const distance =
        (center - viewportHeight / 2) /
        viewportHeight;

      const movement =
        distance * -18;

      element.style.transform =
        `translate3d(0, ${movement}px, 0)`;
    });
  };

  window.addEventListener(
    'scroll',
    throttle(updateParallax, 16),
    {
      passive: true
    }
  );

  updateParallax();
}


/* =========================================
   PERCENTAGE SEEN
   ========================================= */

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;

  const position =
    element.getBoundingClientRect().top +
    scrollY;

  const height = element.offsetHeight;

  if (position > scrollY + viewportHeight) {
    return 0;
  }

  if (position + height < scrollY) {
    return 100;
  }

  const distance =
    scrollY + viewportHeight - position;

  const percentage =
    distance /
    ((viewportHeight + height) / 100);

  return Math.max(
    0,
    Math.min(100, Math.round(percentage))
  );
}


/* =========================================
   THROTTLE
   ========================================= */

function throttle(callback, delay = 16) {
  let waiting = false;

  return (...args) => {
    if (waiting) return;

    waiting = true;

    requestAnimationFrame(() => {
      callback(...args);
      waiting = false;
    });
  };
}


/* =========================================
   INITIALIZE XUYEL ANIMATIONS
   ========================================= */

window.addEventListener(
  'DOMContentLoaded',
  () => {
    initializeScrollAnimationTrigger();
    initializeScrollZoomAnimationTrigger();
    initializeXuyelParallax();
  }
);


/* =========================================
   SHOPIFY THEME EDITOR
   ========================================= */

if (window.Shopify && Shopify.designMode) {

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeScrollAnimationTrigger(
        event.target,
        true
      );

      initializeScrollZoomAnimationTrigger();
      initializeXuyelParallax();
    }
  );

  document.addEventListener(
    'shopify:section:reorder',
    () => {
      initializeScrollAnimationTrigger(
        document,
        true
      );
    }
  );
}